import { Controller } from "@hotwired/stimulus"

/**
 * Drag-and-drop zone backed by a regular file input, so the files submit with the surrounding form.
 *
 * Usage:
 *   <div data-controller="file-drop-zone">
 *     <div data-file-drop-zone-target="zone"
 *          data-action="click->file-drop-zone#open dragover->file-drop-zone#dragOver dragleave->file-drop-zone#dragLeave drop->file-drop-zone#drop">
 *       ...
 *     </div>
 *     <input type="file" multiple class="hidden" data-file-drop-zone-target="input" data-action="change->file-drop-zone#render">
 *     <ul data-file-drop-zone-target="list"></ul>
 *   </div>
 */
export default class extends Controller {
  static targets = ["zone", "input", "list"]

  open() {
    this.inputTarget.click()
  }

  dragOver(event) {
    event.preventDefault()
    this.zoneTarget.classList.add("border-blue-500", "bg-blue-50")
  }

  dragLeave() {
    this.zoneTarget.classList.remove("border-blue-500", "bg-blue-50")
  }

  drop(event) {
    event.preventDefault()
    this.dragLeave()
    this.setFiles([...this.inputTarget.files, ...[...event.dataTransfer.files].filter((file) => this.accepts(file))])
  }

  remove(event) {
    const index = Number(event.currentTarget.dataset.index)
    this.setFiles([...this.inputTarget.files].filter((_, i) => i !== index))
  }

  setFiles(files) {
    const dataTransfer = new DataTransfer()
    files.forEach((file) => dataTransfer.items.add(file))
    this.inputTarget.files = dataTransfer.files
    this.render()
  }

  render() {
    this.listTarget.replaceChildren(...[...this.inputTarget.files].map((file, index) => {
      const item = document.createElement("li")
      item.className = "flex items-center justify-between px-3 py-2 text-sm text-gray-700"

      const name = document.createElement("span")
      name.textContent = file.name

      const button = document.createElement("button")
      button.type = "button"
      button.className = "text-gray-400 hover:text-red-600 cursor-pointer"
      button.textContent = "Remove"
      button.dataset.index = index
      button.dataset.action = "click->file-drop-zone#remove"

      item.append(name, button)
      return item
    }))
  }

  accepts(file) {
    const accept = this.inputTarget.accept
    if (!accept) return true

    const name = file.name.toLowerCase()
    return accept.split(",").some((type) => {
      type = type.trim().toLowerCase()
      return type.startsWith(".") ? name.endsWith(type) : file.type === type
    })
  }
}
